/**
 * Por onde a tela de Ajustes fala com o robô (spec 2026-09-24-robo-painel-de-
 * ajustes, 3.1 e 3.2). Funções puras: a porta, a identificação e o fechamento
 * chegam por parâmetro, então o teste dirige tudo sem navegador.
 */

/** O mínimo que a escolha precisa de uma porta: saber fechar-se. */
export type PortaLike = { fechar(): Promise<void> }

/**
 * Abre a porta e pergunta `INFO`. `null` = não conta: não respondeu, ou é
 * firmware anterior ao `INFO` (`CONFIG_INVALIDA`).
 */
export type AbrirEIdentificar<P> = (porta: P) => Promise<{ mac: string } | null>

async function fecharSemErro(p: PortaLike) {
  try { await p.fechar() } catch { /* já fechada */ }
}

/**
 * A primeira porta cujo `INFO` traz o MAC do cadastro. Uma de cada vez, e para
 * na que casou: as seguintes nem são abertas, porque abrir reinicia a placa
 * CP2102 — o outro robô na mesma mesa não pode reiniciar por causa desta tela.
 * As que não casaram são fechadas na hora.
 */
export async function acharPortaDoRobo<P extends PortaLike>(
  portas: readonly P[], mac: string, abrirEIdentificar: AbrirEIdentificar<P>,
): Promise<P | null> {
  const alvo = mac.toLowerCase()
  for (const porta of portas) {
    let lida: { mac: string } | null = null
    try {
      lida = await abrirEIdentificar(porta)
    } catch {
      lida = null
    }
    if (lida && lida.mac.toLowerCase() === alvo) return porta
    await fecharSemErro(porta)
  }
  return null
}

export type Vinculo<P> =
  | { tipo: 'casou'; porta: P }
  /** Cadastro sem MAC e uma placa só respondendo: perguntar se é este robô. */
  | { tipo: 'confirmar'; porta: P; mac: string }
  /** Cadastro sem MAC e mais de uma placa: a pessoa escolhe; nada foi aberto. */
  | { tipo: 'escolher'; portas: readonly P[] }
  | { tipo: 'nenhuma' }

/**
 * O passo 1 da spec 3.1, com o cadastro que existe hoje (3.2): os robôs de
 * produção têm `mac` nulo, e casar pelo MAC descartaria toda porta. Com `mac`
 * nulo a tela não adivinha.
 */
export async function vincularPlaca<P extends PortaLike>(
  portas: readonly P[], macDoCadastro: string | null, abrirEIdentificar: AbrirEIdentificar<P>,
): Promise<Vinculo<P>> {
  if (portas.length === 0) return { tipo: 'nenhuma' }
  if (macDoCadastro) {
    const porta = await acharPortaDoRobo(portas, macDoCadastro, abrirEIdentificar)
    return porta ? { tipo: 'casou', porta } : { tipo: 'nenhuma' }
  }
  if (portas.length > 1) return { tipo: 'escolher', portas }
  const [porta] = portas
  let lida: { mac: string } | null = null
  try {
    lida = await abrirEIdentificar(porta)
  } catch {
    lida = null
  }
  if (!lida) {
    await fecharSemErro(porta)
    return { tipo: 'nenhuma' }
  }
  return { tipo: 'confirmar', porta, mac: lida.mac }
}

export type Canal = 'usb' | 'rede' | 'nenhum'

/**
 * O que decide se a rede serve (spec 3.1, passo 2):
 * - `ativo`: último contato há menos de 2 min (o veredito `estado` do servidor);
 * - `capaz`: o firmware manda `X-Robo-Ajustes`. `null` = não se sabe, porque o
 *   robô não falou pela rota com credencial própria nos últimos 15 min;
 * - `identidadePropria`: falou pela rota por dispositivo (spec 3.3).
 */
export type EstadoDaRede = { ativo: boolean; capaz: boolean | null; identidadePropria: boolean }

export function redeServe(r: EstadoDaRede): boolean {
  return r.ativo && r.capaz === true && r.identidadePropria
}

/** USB vence: é imediata e não depende do servidor. */
export function escolherCanal({ usb, rede }: { usb: boolean; rede: EstadoDaRede }): Canal {
  if (usb) return 'usb'
  return redeServe(rede) ? 'rede' : 'nenhum'
}

/**
 * Por que a rede não serve, na ordem da spec 3.1: sem contato, firmware sem
 * ajustes, credencial compartilhada. `capaz` nulo não acusa o firmware — sem
 * retrato, o que se sabe é que ele não falou com credencial própria.
 * `semContatoHaS`: segundos desde o último contato, quando houve um.
 */
export function motivoSemRede(r: EstadoDaRede, semContatoHaS: number | null = null): string | null {
  if (redeServe(r)) return null
  if (!r.ativo) {
    if (semContatoHaS === null) return 'nunca falou com o Console'
    return `sem contato há ${duracao(semContatoHaS)}`
  }
  if (r.capaz === false) return 'firmware sem ajustes a distância'
  return 'credencial compartilhada'
}

/** "40 s", "7 min", "3 h". */
export function duracao(segundos: number): string {
  const s = Math.max(0, Math.round(segundos))
  if (s < 60) return `${s} s`
  if (s < 3600) return `${Math.floor(s / 60)} min`
  if (s < 86400) return `${Math.floor(s / 3600)} h`
  return `${Math.floor(s / 86400)} d`
}
